import {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState,
} from "react";
import { hashKey } from "@tanstack/react-query";
import { ZodSchema } from "zod";
import { Task } from "@common/types/task";
import { View } from "@common/types/views/views";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { useTaskColumnResizing } from "@hooks/useTaskColumnResizing";
import { withBulkActions } from "./taskTableView";

interface UseTaskTableViewStateParams<TView extends View> {
    selectedTask: Task;
    viewSchema: ZodSchema<TView>;
    parseView: (raw: Task["view"]) => TView;
}

export function useTaskTableViewState<TView extends View>({
    selectedTask,
    viewSchema,
    parseView,
}: UseTaskTableViewStateParams<TView>) {
    const { applyColumnSizesToView, onViewChange: persistColumnSizes } =
        useTaskColumnResizing(selectedTask.id);

    const taskViewKey = useMemo(
        () => hashKey([selectedTask.view]),
        [selectedTask.view],
    );
    // Keyed on the view content: a structurally identical refetch must not
    // discard the user's local edits.
    const taskView = useMemoByKeys(
        () => withBulkActions(parseView(selectedTask.view)),
        [taskViewKey],
    );

    const [view, setView] = useState<TView>(taskView);

    const resetView = useEffectEvent(() => {
        setView(taskView);
    });
    // Re-sync only when the task or its view content changes.
    useEffect(() => {
        resetView();
    }, [selectedTask.id, taskViewKey]);

    const handleViewChange = useCallback(
        (updated: View) => {
            persistColumnSizes(updated);
            setView((previous) => {
                const parsed = viewSchema.safeParse({
                    ...previous,
                    columns: updated.columns,
                    options: {
                        ...updated.options,
                        selection: previous.options?.selection,
                        bulkActions: previous.options?.bulkActions,
                    },
                });
                return parsed.success ? parsed.data : previous;
            });
        },
        [persistColumnSizes, viewSchema],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>((payload) => {
        setView((previous) => ({
            ...previous,
            filters: { ...previous.filters, ...payload },
        }));
    }, []);

    const sizedView = useMemo(
        () => applyColumnSizesToView(view),
        [applyColumnSizesToView, view],
    );
    const allowedColumnIds = useMemo(
        () => new Set(taskView.columns.map((column) => column.id)),
        [taskView.columns],
    );

    return {
        taskView,
        taskViewKey,
        sizedView,
        allowedColumnIds,
        handleViewChange,
        handleFiltersChange,
    };
}
